import { useLayoutEffect, useRef } from "react"

import { Box, SvgIcon as MUISvgIcon, Stack } from "@mui/material"

import { gsap } from "@ggs/ui/local/gsap"
import type { LinkType } from "../../graphql/types"
import { Breadcrumbs, type FC, type FCWC, type ResponsiveImageType } from ".."
import HeroMedia from "./HeroMedia"

type HeroProps = {
  media?: ResponsiveImageType[]
  path?: LinkType[]
}

const HeroBlog: FC<HeroProps> = ({ path, media }) => {
  return (
    <HeroContainer>
      <HeroMedia media={media} />
      <HeroContent>
        {path && path.length > 0 && <Breadcrumbs items={path} />}
        <GlobetrottingLogo />
        <div style={{ display: "none" }}>
          <h1>An inspirational travel blog featuring travel stories and articles.</h1>          
        </div>
      </HeroContent>
    </HeroContainer>
  )
}

export default HeroBlog

/** A sub-component for the section container of the hero content */
const HeroContainer: FCWC = ({ children, ...props }) => (
  <Stack
    component="header"
    alignItems="stretch"
    justifyContent="flex-end"
    width="100%"
    position="relative"
    height={{ xs: 700, md: 880 }}
    sx={{ isolation: "isolate" }}
    {...props}
  >
    {children}
  </Stack>
)

/** A sub-component for the container of the hero content */
const HeroContent: FCWC<{}> = ({ children }) => (
  <Stack
    alignItems="center"
    justifyContent="flex-end"
    rowGap={{ xs: 5, md: 10 }}
    height={{ xs: 636 /* 708 - 64 */, md: 750 /* 890 - 130 */ }}
    p={3}
    pb={10}
    position="relative"
    zIndex={3}
  >
    {children}
  </Stack>
)

const GlobetrottingLogo = () => {
  const logoRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(logoRef.current, {
        transform: "translateY(-50%) scale(50%)",
        scrollTrigger: {
          start: "top top",
          end: "bottom 75%",
          scrub: true,
          // markers: true,
        },
      })
    })
    return () => ctx.revert()
  }, [logoRef])

  return (
    <Box ref={logoRef} width="100%" px={{ md: 6.5 }}>
      <MUISvgIcon
        viewBox="0 0 1576 235"
        sx={(theme) => ({
          width: "100%",
          height: "auto",
          fill: theme.palette["neutral.100"].main,
        })}
      >
        <path d="M26 108c0-52 41-85 94-85 20 0 31 5 39 5 5 0 7-4 5-10l5-1 14 51-5 1c-15-31-36-41-55-41-33 0-51 29-51 79 0 51 20 77 51 77 8 0 16-2 23-6v-44c0-14-5-17-19-17v-4h69v4c-9 0-12 3-12 16v37c-17 12-41 19-66 19-51 0-92-28-92-81Zm190 45V25h-13v-4c17 0 38-7 46-20h4v152c0 22 1 28 13 28v4h-64v-4c13 0 14-6 14-28Zm57-25c0-41 31-64 68-64 39 0 66 24 66 62 0 41-32 64-69 64-38-1-65-24-65-62Zm95 1c0-42-12-60-29-60-18 0-28 21-28 56 0 42 12 60 29 60 18 0 28-21 28-56Zm72 50c-8 0-12 3-17 9l-4-3c5-7 7-11 7-32V24h-14v-4c16 0 39-7 46-20h4v88h1c12-16 25-24 41-24 27 0 47 18 47 57 0 46-30 68-64 68-24 0-33-10-47-10Zm75-49c0-35-12-47-28-47-9 0-16 3-25 12v80c5 5 12 10 22 10 18 0 31-19 31-55Zm47 0c0-42 32-66 65-66 30 0 49 18 49 39 0 7-4 10-16 10h-61v4c0 33 15 54 40 54 16 0 29-9 40-27l3 2c-13 28-31 43-61 43-34 0-59-22-59-59Zm64-21c11 0 15-2 15-13 0-14-5-27-18-27-15 0-22 20-24 40h27Zm72 46V73h-13v-4c26-1 38-14 46-36h3v35h30v5h-30v85c0 13 6 18 13 18s12-6 17-14l4 2c-9 16-20 26-38 26-19-1-32-11-32-35Zm87-3V87h-13v-5c16 0 38-7 46-20h4v36c8-20 19-34 34-34 10 0 16 7 16 19s-7 17-14 17c-9 0-14-10-21-10s-12 8-15 20v42c0 23 2 29 13 29h4v4h-67v-4c11 0 13-6 13-29Zm91-24c0-41 32-64 69-64 38 0 65 24 65 62 0 41-31 64-68 64-39-1-66-24-66-62Zm96 1c0-42-12-60-29-60-19 0-28 21-28 56 0 42 12 60 29 60 18 0 28-21 28-56Zm58 26V73h-12v-4c25-1 37-14 45-36h4v35h29v5h-29v85c0 13 5 18 12 18s12-6 17-14l4 2c-9 16-20 26-38 26-19-1-32-11-32-35Zm86 0V73h-12v-4c25-1 37-14 45-36h3v35h30v5h-30v85c0 13 6 18 13 18s12-6 17-14l4 2c-9 16-20 26-38 26-19-1-32-11-32-35Zm86-2V87h-13v-5c17 0 38-7 45-20h4v91c0 22 1 28 14 28v4h-64v-4c12 0 14-6 14-28Zm-3-124c0-11 9-20 20-20 10 0 19 9 19 20s-9 19-19 19c-11 0-20-8-20-19Zm159 123v-44c0-18-7-25-19-25s-22 7-29 16v54c0 22 1 28 13 28v4h-63v-4c12 0 14-6 14-28V87h-14v-5c17 0 39-7 46-20h4v29h1c10-13 28-27 48-27 22 0 36 13 36 44v45c0 22 1 28 14 28v5h-63v-5c10 0 12-7 12-29Zm182-70 10-35-35 9 18 9 7 17Zm-66-16c16 0 22 16 22 34s-6 34-22 34-21-16-21-34 5-34 21-34Zm3 87c-29-1-39-5-38-18 9 4 21 6 35 6 39 0 57-17 57-40 0-13-5-24-17-32l-2-1-1-1h-1c-5-3-12-6-20-7h-6l-1-1h-9c-40 0-57 19-57 42 0 13 5 24 17 31-9 4-17 13-17 24 0 20 17 27 47 29 22 2 46 4 46 20s-17 22-34 23c-22 1-40-16-27-38-20 1-32 9-32 20 0 15 21 25 58 25 46 0 71-17 71-45s-28-35-69-37Z" />
      </MUISvgIcon>
    </Box>
  )
}
